
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import { defineComponent, PropType } from "vue";
import { mapState } from "vuex";
import {
  IAutoMLModel,
  METRIC_LIST,
  ENSEMBLE_MODEL,
  SMALLEST_BEST_METRICS,
} from "../../types";
import {
  DatasourceConfig,
  ExperimentStatus,
  IExperiment,
} from "@/pages/Experiment/types";
import { toShortNumber } from "@/plugin/utils/shortNumber";

use([CanvasRenderer, BarChart]);

export default defineComponent({
  props: {
    models: {
      type: Object as PropType<IAutoMLModel[]>,
    },
    experiment: {
      type: Object as PropType<IExperiment>,
    },
    datasourceConfig: {
      type: Object as PropType<DatasourceConfig>,
    },
  },
  data(): {
    selectedModel: string;
  } {
    return {
      selectedModel: "",
    };
  },
  created() {
    this.selectedModel = this.listModels[0];
  },
  watch: {
    models() {
      this.selectedModel = this.listModels[0];
    },
  },
  computed: {
    ...mapState("application", ["theme"]),
    experimentStatus(): number {
      switch (this.experiment?.status) {
        case ExperimentStatus.Completed:
          return 1;
        case ExperimentStatus.Failed:
        case ExperimentStatus.Canceled:
          return -1;
        default:
          return 0;
      }
    },
    primaryMetric(): string {
      return METRIC_LIST[this.experiment?.config?.primaryMetric] || "";
    },
    listModels(): string[] {
      if (this.models && this.models.length > 0) {
        let models = this.models;
        models.sort((prev, curr) => {
          const primaryMetric = this.experiment?.config?.primaryMetric;
          const scoreType =
            this.experimentStatus == 1 ? "testScore" : "valScore";
          if (prev[scoreType] && curr[scoreType]) {
            if (SMALLEST_BEST_METRICS[primaryMetric]) {
              return prev[scoreType][primaryMetric] <
                curr[scoreType][primaryMetric]
                ? -1
                : 1;
            } else {
              return prev[scoreType][primaryMetric] >
                curr[scoreType][primaryMetric]
                ? -1
                : 1;
            }
          }
          return 0;
        });
        // Do task 1554 - If WeightedEnsemble was best model, shows feature importance of largest weight model
        if (models[0].modelType === ENSEMBLE_MODEL) {
          const bestModelName = Object.entries(
            models[0].featureImportance
          ).sort((prev, curr) => {
            return prev[1] > curr[1] ? -1 : 1;
          })[0][0];
          const bestModel = models.find((e) => e.name === bestModelName);
          if (bestModel) {
            const bestModelIndex = models.findIndex(
              (e) => e.name === bestModelName
            );
            models.splice(bestModelIndex, 1);
            models.unshift(bestModel);
          }
        }
        return models
          ?.filter((value) => {
            return (
              value.modelType !== ENSEMBLE_MODEL &&
              value.featureImportance &&
              Object.entries(value.featureImportance).length > 0
            );
          })
          .map((model) => {
            return model.name;
          });
      }
      return [""];
    },
    model(): IAutoMLModel | null {
      if (this.models?.length) {
        for (const value of this.models) {
          if (value.name === this.selectedModel) {
            return value;
          }
        }
      }
      return null;
    },
    data(): string[][] {
      if (this.model) {
        let data = [["feature", "value"]]; // dimension definition
        data.push(
          ...Object.entries(this.model.featureImportance).map((entry) => {
            const percent = entry[1] * 100;
            return [entry[0], percent.toFixed(2)];
          })
        );
        return data;
      }
      return [];
    },
    option(): unknown {
      return {
        series: [
          {
            type: "bar",
            encode: { y: "feature", x: "value" },
            datasetIndex: 1,
            label: {
              show: true,
              color: "var(--primary-color)",
              position: "right",
              formatter: (data: any) => {
                return Math.round(data.value[1]) + "%";
              },
            },
          },
        ],
        dataset: [
          {
            source: this.data,
          },
          {
            transform: {
              type: "sort",
              config: { dimension: "value", order: "asc" },
            },
          },
        ],
        dataZoom: [
          {
            type: "inside",
            orient: "vertical",
            start: this.data.length <= 11 ? 0 : 30,
            end: 100,
          },
          {
            type: "slider",
            orient: "vertical",
            right: "3%",
          },
        ],
        yAxis: {
          type: "category",
        },
        grid: {
          left: "3%",
          right: "16%",
          bottom: "3%",
          top: "2%",
          containLabel: true,
        },
        xAxis: {
          type: "value",
          axisLabel: {
            formatter: (value: number) => {
              return value + "%";
            },
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "line",
          },
          formatter: (params: any) => {
            return `<div>${params[0].value[0]}: <strong>${Math.round(
              params[0].value[1]
            )}%</strong></div>`;
          },
        },
      };
    },
    title(): string {
      return this.$t("automl.evaluation.chart.feature.title", {
        model: this.model?.name,
        top: this.data.length - 1,
        target: this.datasourceConfig?.targetColumn,
      });
    },
    testScore(): string {
      return this.$t("automl.evaluation.chart.test", {
        metric: this.primaryMetric,
        score:
          this.model?.testScore &&
          toShortNumber(this.model.testScore[this.model.primaryMetric]),
      });
    },
    valScore(): string {
      return this.$t("automl.evaluation.chart.validation", {
        metric: this.primaryMetric,
        score:
          this.model?.valScore &&
          toShortNumber(this.model.valScore[this.model.primaryMetric]),
      });
    },
  },
});
